import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  SafeAreaView,
  TouchableOpacity,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  TextInput,
  Image
} from "react-native";
import { useTranslation } from "react-i18next";
import type { TextInput as RNTextInput } from "react-native";
import { consumerLookup } from "../../../../../services/send.service";
import { SessionState, useSessionStore } from "../../../../../../../state/src/useSessionStore";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";


type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export type EnterPhoneStepHandle = {
  submit: () => Promise<'ok' | 'notfound'>;
};

const EnterPhoneStep = forwardRef<EnterPhoneStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t } = useTranslation();
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setPhoneNumberStore = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const [phoneNumber, setPhoneNumber] = useState("");
  const inputRef = useRef<RNTextInput | null>(null);

  // Focus input automatically when component mounts (ensures mobile keyboard shows)
  // useEffect(() => {
  //   const id = setTimeout(() => inputRef.current?.focus(), 100);
  //   return () => clearTimeout(id);
  // }, []);

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const submit = async (): Promise<'ok' | 'notfound'> => {
    if (phoneNumber.length !== 10) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
      throw new Error('Invalid phone number');
    }
    if (!agentId || !unitProfileId) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
      throw new Error('Missing agent or unit profile');
    }
    try {
      const ConsumerLookupInfo = await consumerLookup(phoneNumber, agentId, unitProfileId);
      console.log("consumerLookup Response:", ConsumerLookupInfo);
      try { setIsNewConsumer?.(false); } catch {}
      return 'ok';
    } catch (error: any) {
      const code = error?.code || error?.ErrorDetail?.ErrorCode;
      if (String(code) === "616") {
        try { setIsNewConsumer?.(true); } catch {}
        return 'notfound';
      }
      try { setIsNewConsumer?.(false); } catch {}
      Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
      throw error;
    } finally {
      console.log('')
    }
  };

  useImperativeHandle(ref, () => ({ submit }));

  const handlePhoneChange = (text: string) => {
    const cleaned = text.replace(/\D/g, "");
    if (cleaned?.length <= 10) {
      setPhoneNumber(cleaned);
      try { setPhoneNumberStore?.(cleaned.length === 10 ? cleaned : null); } catch {}
      // Tell parent if valid
      onValidationChange?.(cleaned?.length === 10);
    }
  };

  const Wrapper = Platform.OS === "web" ? View : SafeAreaView;
  return (
    <Wrapper style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <View>
          <ScrollView
            contentContainerStyle={[styles.scrollContainer, { flexGrow: 1 }]}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <View style={styles.content}>
              <Text style={styles.title}>{t("PHONE_STEP_TITLE")}</Text>
              <Text style={styles.subtitle}>{t("PHONE_STEP_SUBTITLE")}</Text>
              <View style={styles.phoneInputContainer}>
                <View style={styles.countryCodeSelector}>
                  <Image
                    source={EnglishFlag}
                    style={{height: 24, width: 24, borderRadius: 50}}
                    resizeMode='contain'           
                  />
                  <Text style={styles.plusText}>+1</Text>
                </View>
                <FloatingTextInputComponent
                  label={t("PHONE_STEP_PLACEHOLDER")}
                  value={formatPhoneNumber(phoneNumber)}
                  onChangeText={handlePhoneChange}
                 />
                {/* <View style={styles.phoneNumberInput}>
                  <TextInput
                    // ref={inputRef}
                    style={styles.textInput}
                    placeholder={t("PHONE_STEP_PLACEHOLDER")}
                    placeholderTextColor="#9CA3AF"
                    keyboardType="numeric"
                    maxLength={12}
                    value={formatPhoneNumber(phoneNumber)}
                    onChangeText={handlePhoneChange}
                    returnKeyType="done"
                    textContentType="telephoneNumber"
                    autoComplete="tel"
                    autoCorrect={false}
                    autoFocus
                  />
                </View> */}
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Wrapper>
  );
});

export default EnterPhoneStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff"
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "stretch",
    justifyContent: "flex-start"
  },
  content: { paddingTop: 8  },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: "#111827",
    textAlign: "left",
    marginBottom: 6
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    textAlign: "left",
    marginBottom: 30
  },
  separatorLine: { backgroundColor: "#E5E7EB", height: 1, marginVertical: 15 },
  bottomBar: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 0,
    backgroundColor: "#fff"
  },
  continueButton: {
    backgroundColor: "#000",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    marginTop: 10,
  },
  continueButtonDisabled: {
    backgroundColor: "#666"
  },
  continueButtonText: { color: "#fff", fontSize: 18, fontWeight: "bold" },
  phoneInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
},
countryCodeSelector: {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: "#F3F4F6",
  borderRadius: 8,
  paddingHorizontal: 12,
  paddingVertical: 12,
  marginRight: 8,
  // borderWidth: 1,
  borderColor: "#E5E7EB",
},

countryText: {
  fontSize: 14,
  fontWeight: "600",
  color: "#111827",
  marginRight: 4,
},

plusText: {
  fontSize: 14,
  fontWeight: "700",
  color: "#111827",
},

phoneNumberInput: {
  flex: 1,
},

textInput: {
  backgroundColor: "#F9FAFB",
  borderRadius: 12,
  borderWidth: 1,
  borderColor: "#D1D5DB",
  paddingHorizontal: 14,
  paddingVertical: Platform.OS === "ios" ? 12 : 10,
  fontSize: 16,
  fontWeight: "600",
  color: "#111827",
},
});